import { defaultMyGoalsBoardFilters } from '@helpers/constants';
import { demoUser, reportStorageUrl } from '@http/constants';
import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import type {
  AccessibilityData,
  AuditData,
  Responsibility,
  WebsiteData,
  TranslateData,
  WcagCheckerData,
  UserData,
} from '@eye-able/types';
import type { GraphTimeRange } from './pages/report/reportComponents/charts/types';

// UI
export const leftDrawerOpenAtom = atom(false);
export const activeNavItemAtom = atom('');

let isDemo = false;

if (window.location.pathname === '/demo') {
  window.localStorage.setItem('demoMode', 'true');
  isDemo = true;
} else {
  window.localStorage.setItem('demoMode', 'false');
}

// Demo Mode for the whole dashboard, has the demo user logged in
export const demoModeAtom = atomWithStorage('demoMode', isDemo);

interface StorageValues {
  accordionExpanded: Record<string, unknown>;
  checkerLicenses: unknown[];
  cookieBannerAccepted: boolean;
  currentAuditEntryId: number | null;
  matomoOptIn: boolean;
  accessDataPrivacyBannerDismissed: boolean;
  accessInstallationBannerDismissed: boolean;
  allyPromptBannerDismissed: boolean;
  myGoalsBoardFilters: typeof defaultMyGoalsBoardFilters;
  notificationLastViewDate: number;
  reactTableFilters: Record<string, unknown>;
  reactTableSorting: Record<string, unknown>;
  rerenderLayout: string;
  selectedAssistDomain: string;
  selectedGraphTimeRange: GraphTimeRange;
  spellCheckColumnFilters: unknown[];
  spellCheckColumnSorting: Record<string, unknown>[];
  spellCheckDictColumnFilters: unknown[];
  spellCheckDictColumnSorting: Record<string, unknown>[];
  spellCheckSiteDetailColumnFilters: unknown[];
  spellCheckSiteDetailColumnSorting: Record<string, unknown>[];
  spellCheckSitesColumnFilters: unknown[];
  spellCheckSitesColumnSorting: Record<string, unknown>[];
  tokenExpires: number;
  userToursSeen: unknown[];
  versionOfLastNotificationSeen: string;
  websiteLicenses: unknown[];
}

const sessionStorageKeys = new Set<keyof StorageValues>([
  'currentAuditEntryId',
]);

function getStorage(key: keyof StorageValues): Storage {
  return sessionStorageKeys.has(key)
    ? window.sessionStorage
    : window.localStorage;
}

function getItem(key: keyof StorageValues): string | null {
  return getStorage(key).getItem(key);
}

const rawStorage: Record<keyof StorageValues, string | null> = {
  accordionExpanded: getItem('accordionExpanded'),
  checkerLicenses: getItem('checkerLicenses'),
  cookieBannerAccepted: getItem('cookieBannerAccepted'),
  currentAuditEntryId: getItem('currentAuditEntryId'),
  matomoOptIn: getItem('matomoOptIn'),
  accessDataPrivacyBannerDismissed: getItem('accessDataPrivacyBannerDismissed'),
  accessInstallationBannerDismissed: getItem(
    'accessInstallationBannerDismissed',
  ),
  allyPromptBannerDismissed: getItem('allyPromptBannerDismissed'),
  myGoalsBoardFilters: getItem('myGoalsBoardFilters'),
  notificationLastViewDate: getItem('notificationLastViewDate'),
  reactTableFilters: getItem('reactTableFilters'),
  reactTableSorting: getItem('reactTableSorting'),
  rerenderLayout: getItem('rerenderLayout'),
  selectedAssistDomain: getItem('selectedAssistDomain'),
  selectedGraphTimeRange: getItem('selectedGraphTimeRange'),
  spellCheckColumnFilters: getItem('spellCheckColumnFilters'),
  spellCheckColumnSorting: getItem('spellCheckColumnSorting'),
  spellCheckDictColumnFilters: getItem('spellCheckDictColumnFilters'),
  spellCheckDictColumnSorting: getItem('spellCheckDictColumnSorting'),
  spellCheckSiteDetailColumnFilters: getItem(
    'spellCheckSiteDetailColumnFilters',
  ),
  spellCheckSiteDetailColumnSorting: getItem(
    'spellCheckSiteDetailColumnSorting',
  ),
  spellCheckSitesColumnFilters: getItem('spellCheckSitesColumnFilters'),
  spellCheckSitesColumnSorting: getItem('spellCheckSitesColumnSorting'),
  tokenExpires: getItem('tokenExpires'),
  userToursSeen: getItem('userToursSeen'),
  versionOfLastNotificationSeen: getItem('versionOfLastNotificationSeen'),
  websiteLicenses: getItem('websiteLicenses'),
};

function clone<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

let isCorruptionHandled = false;

function handleCorruptedStorage(
  error: unknown,
  key: keyof StorageValues,
): void {
  if (isCorruptionHandled) return;

  isCorruptionHandled = true;
  console.error('Invalid JSON in store!', error, key);

  window.localStorage.clear();
  window.sessionStorage.clear();
  window.location.replace('/authentication/login');
}

function parse<T>(
  key: keyof StorageValues,
  value: string | null,
  fallback: () => T,
): T {
  if (value === null) return fallback();

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    handleCorruptedStorage(error, key);
    return fallback();
  }
}

// Note: Fallbacks as functions defer execution until needed and ensure
// composite defaults are not shared references.
const localValues: StorageValues = {
  rerenderLayout: parse('rerenderLayout', rawStorage.rerenderLayout, () =>
    Date.now().toString(),
  ),
  currentAuditEntryId: parse(
    'currentAuditEntryId',
    rawStorage.currentAuditEntryId,
    () => null,
  ),
  tokenExpires: parse('tokenExpires', rawStorage.tokenExpires, () => 0),
  websiteLicenses: parse(
    'websiteLicenses',
    rawStorage.websiteLicenses,
    () => [],
  ),
  checkerLicenses: parse(
    'checkerLicenses',
    rawStorage.checkerLicenses,
    () => [],
  ),
  selectedAssistDomain: parse(
    'selectedAssistDomain',
    rawStorage.selectedAssistDomain,
    () => '',
  ),
  selectedGraphTimeRange: parse(
    'selectedGraphTimeRange',
    rawStorage.selectedGraphTimeRange,
    () => 'threeMonths',
  ),
  cookieBannerAccepted: parse(
    'cookieBannerAccepted',
    rawStorage.cookieBannerAccepted,
    () => false,
  ),
  matomoOptIn: parse('matomoOptIn', rawStorage.matomoOptIn, () => true),
  accessDataPrivacyBannerDismissed: parse(
    'accessDataPrivacyBannerDismissed',
    rawStorage.accessDataPrivacyBannerDismissed,
    () => false,
  ),
  allyPromptBannerDismissed: parse(
    'allyPromptBannerDismissed',
    rawStorage.allyPromptBannerDismissed,
    () => false,
  ),
  accessInstallationBannerDismissed: parse(
    'accessInstallationBannerDismissed',
    rawStorage.accessInstallationBannerDismissed,
    () => false,
  ),
  versionOfLastNotificationSeen: parse(
    'versionOfLastNotificationSeen',
    rawStorage.versionOfLastNotificationSeen,
    () => '1.0.0',
  ),
  notificationLastViewDate: parse(
    'notificationLastViewDate',
    rawStorage.notificationLastViewDate,
    () => 0,
  ),
  userToursSeen: parse('userToursSeen', rawStorage.userToursSeen, () => []),
  myGoalsBoardFilters: parse(
    'myGoalsBoardFilters',
    rawStorage.myGoalsBoardFilters,
    () => clone(defaultMyGoalsBoardFilters),
  ),
  reactTableSorting: parse(
    'reactTableSorting',
    rawStorage.reactTableSorting,
    () => ({}),
  ),
  reactTableFilters: parse(
    'reactTableFilters',
    rawStorage.reactTableFilters,
    () => ({}),
  ),
  accordionExpanded: parse(
    'accordionExpanded',
    rawStorage.accordionExpanded,
    () => ({}),
  ),
  spellCheckColumnSorting: parse(
    'spellCheckColumnSorting',
    rawStorage.spellCheckColumnSorting,
    () => [],
  ),
  spellCheckSitesColumnSorting: parse(
    'spellCheckSitesColumnSorting',
    rawStorage.spellCheckSitesColumnSorting,
    () => [],
  ),
  spellCheckDictColumnSorting: parse(
    'spellCheckDictColumnSorting',
    rawStorage.spellCheckDictColumnSorting,
    () => [],
  ),
  spellCheckSiteDetailColumnSorting: parse(
    'spellCheckSiteDetailColumnSorting',
    rawStorage.spellCheckSiteDetailColumnSorting,
    () => [],
  ),
  spellCheckColumnFilters: parse(
    'spellCheckColumnFilters',
    rawStorage.spellCheckColumnFilters,
    () => [],
  ),
  spellCheckSitesColumnFilters: parse(
    'spellCheckSitesColumnFilters',
    rawStorage.spellCheckSitesColumnFilters,
    () => [],
  ),
  spellCheckDictColumnFilters: parse(
    'spellCheckDictColumnFilters',
    rawStorage.spellCheckDictColumnFilters,
    () => [],
  ),
  spellCheckSiteDetailColumnFilters: parse(
    'spellCheckSiteDetailColumnFilters',
    rawStorage.spellCheckSiteDetailColumnFilters,
    () => [],
  ),
};

export const tokenExpiresAtom = atomWithStorage(
  'tokenExpires',
  localValues.tokenExpires,
);

export const rerenderLayoutAtom = atomWithStorage(
  'rerenderLayout',
  localValues.rerenderLayout,
);

// Demo Modes for specific products, only true if user is logged in but has no license for the product.
// Always true when in general demo mode. Otherwise it may be set by the license query
export const reportDemoModeAtom = atom(isDemo);
export const accStatementDemoModeAtom = atom(isDemo);
export const auditDemoModeAtom = atom(isDemo);
export const assistDemoModeAtom = atom(isDemo);
export const translateDemoModeAtom = atom(isDemo);
export const easyDemoModeAtom = atom(isDemo);

// Current User
export const userAtom = atom<UserData | typeof demoUser | undefined>(
  isDemo ? demoUser : undefined,
);
// real current user (fully typed), not the demo user
export const userDataAtom = atom<UserData | undefined>((get) => {
  const user = get(userAtom);
  if (!user) return undefined;
  if (user === demoUser) return undefined;
  return user as UserData;
});

export const assistDomainAtom = atomWithStorage(
  'selectedAssistDomain',
  localValues.selectedAssistDomain,
);

export const auditLicensesAtom = atom<WcagCheckerData[]>([]);
export const assistLicensesAtom = atom<WebsiteData[]>([]);
export const availableAuditEntriesAtom = atom<AuditData[]>([]);
export const translateEasyLicenses = atom<TranslateData[]>([]);
export const issueContentLicensesAtom = atom<Responsibility[]>([]);
export const issuesTableFiltersAtom = atom([]);
export const bookmarksAtom = atom([]);
export const orgUsersAtom = atom([]);
export const todosDataAtom = atom({ todos: {} });
export const jiraConfigAtom = atom(null);
export const userNotificationsAtom = atom([]);
export const accessibilityDataAtom = atom<AccessibilityData | null>(null);
export const primaryReportDateAtom = atom(null);
export const secondaryReportDateAtom = atom(null);

export const currentGuidedTestNoAtom = atom(null);
export const activeGuidedTestNoAtom = atom(null);

export type TransSelectedDomain = {
  id: number | string;
  domain: string;
  usedChars?: number | string | null;
  charLimit?: number | string | null;
  usedCharsEasy?: number | string | null;
  charLimitEasy?: number | string | null;
  usage?: string;
  created?: string;
};

export const transSelectedDomainAtom = atomWithStorage<
  TransSelectedDomain | ''
>('transSelectedDomain', '');

const auditEntrySessionStorage = createJSONStorage<number | null>(
  () => window.sessionStorage,
);

export const currentAuditEntryIdAtom = atomWithStorage(
  'currentAuditEntryId',
  localValues.currentAuditEntryId,
  auditEntrySessionStorage,
);

export const currentAuditEntryAtom = atom((get) => {
  const currentAuditEntryId = get(currentAuditEntryIdAtom);
  const availableAuditEntries = get(availableAuditEntriesAtom);

  if (!currentAuditEntryId || !availableAuditEntries?.length) return null;

  return (
    availableAuditEntries.find((entry) => {
      return entry.id === currentAuditEntryId;
    }) ?? null
  );
});

export const selectedDomainAtom = atom((get) => {
  const entry = get(currentAuditEntryAtom);

  if (!entry) return null;

  return entry.domain;
});

export const currentCdnDirectoryAtom = atom((get) => {
  const currentAuditEntry = get(currentAuditEntryAtom);

  if (!currentAuditEntry) return null;

  return currentAuditEntry.cdnDirectory;
});

export const fullCdnPathAtom = atom((get) => {
  const currentCdnDirectory = get(currentCdnDirectoryAtom);
  if (!currentCdnDirectory) {
    return null;
  }

  return (
    reportStorageUrl +
    currentCdnDirectory +
    (currentCdnDirectory.endsWith('/') ? '' : '/')
  );
});

export const fullUsersCdnPathAtom = atom((get) => {
  const user = get(userAtom);
  if (!user) return null;

  return `${reportStorageUrl}org_storage/${user.primaryOrganisation}/PDF/uploadedPDFs/`;
});

export const selectedGraphTimeRangeAtom = atomWithStorage(
  'selectedGraphTimeRange',
  localValues.selectedGraphTimeRange,
);

export const cookieBannerAcceptedAtom = atomWithStorage(
  'cookieBannerAccepted',
  localValues.cookieBannerAccepted,
);

export const matomoOptInAtom = atomWithStorage(
  'matomoOptIn',
  localValues.matomoOptIn,
);

export const accessDataPrivacyBannerDismissedAtom = atomWithStorage(
  'accessDataPrivacyBannerDismissed',
  localValues.accessDataPrivacyBannerDismissed,
);

export const accessInstallationBannerDismissedAtom = atomWithStorage(
  'accessInstallationBannerDismissed',
  localValues.accessInstallationBannerDismissed,
);

export const allyPromptBannerDismissedAtom = atomWithStorage(
  'allyPromptBannerDismissed',
  localValues.allyPromptBannerDismissed,
);

export const versionOfLastNotificationSeenAtom = atomWithStorage(
  'versionOfLastNotificationSeen',
  localValues.versionOfLastNotificationSeen,
);

export const notificationLastViewDateAtom = atomWithStorage(
  'notificationLastViewDate',
  localValues.notificationLastViewDate,
);

export const userToursSeenAtom = atomWithStorage(
  'userToursSeen',
  localValues.userToursSeen,
);

export const reactTableFiltersAtom = atomWithStorage(
  'reactTableFilters',
  localValues.reactTableFilters,
);

export const accordionExpandedAtom = atomWithStorage(
  'accordionExpanded',
  localValues.accordionExpanded,
);

export const reactTableSortingAtom = atomWithStorage(
  'reactTableSorting',
  localValues.reactTableSorting,
);

export const myGoalsFiltersAtom = atomWithStorage(
  'defaultMyGoalsBoardFilters',
  {
    ...localValues.myGoalsBoardFilters,
    searchText: '',
  },
);

export const spellCheckColumnSortingAtom = atomWithStorage(
  'spellCheckColumnSorting',
  localValues.spellCheckColumnSorting.length
    ? localValues.spellCheckColumnSorting
    : [{ id: 'spellcheckingOccurrences', desc: true }],
);

export const spellCheckSitesColumnSortingAtom = atomWithStorage(
  'spellCheckSitesColumnSorting',
  localValues.spellCheckSitesColumnSorting.length
    ? localValues.spellCheckSitesColumnSorting
    : [{ id: 'pageMistakesCount', desc: true }],
);

export const spellCheckSiteDetailColumnSortingAtom = atomWithStorage(
  'spellCheckSiteDetailColumnSorting',
  localValues.spellCheckSiteDetailColumnSorting.length
    ? localValues.spellCheckSiteDetailColumnSorting
    : [{ id: 'spellcheckingOccurrences', desc: true }],
);

export const spellCheckDictColumnSortingAtom = atomWithStorage(
  'spellCheckDictColumnSorting',
  localValues.spellCheckDictColumnSorting.length
    ? localValues.spellCheckDictColumnSorting
    : [{ id: 'spellcheckingOccurrences', desc: true }],
);

export const spellCheckColumnFiltersAtom = atomWithStorage(
  'spellCheckColumnFilters',
  localValues.spellCheckColumnFilters,
);

export const spellCheckSitesColumnFiltersAtom = atomWithStorage(
  'spellCheckSitesColumnFilters',
  localValues.spellCheckSitesColumnFilters,
);

export const spellCheckSiteDetailColumnFiltersAtom = atomWithStorage(
  'spellCheckSiteDetailColumnFilters',
  localValues.spellCheckSiteDetailColumnFilters,
);

export const spellCheckDictColumnFiltersAtom = atomWithStorage(
  'spellCheckDictColumnFilters',
  localValues.spellCheckDictColumnFilters,
);
